import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { NotificationRuleFormData } from '@/features/notification-rules/schemas/create-notification-rule.schema.ts';
import { Button } from '@/components/ui/button.tsx';
import { Plus, Trash2 } from 'lucide-react';
import { StatusAlert } from '@/features/notification-rules/components/create-notification-rule/status-alert.tsx';
import { cn } from '@/utils/utils.ts';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { OPERATORS } from '@/features/notification-rules/schemas/notification-rules.utils.ts';
import { ParameterComboboxSelect } from '@/features/rules-shared/components/parameter-combobox-select.tsx';
import { ConditionValueBasedOnParameter } from '@/features/rules-shared/components/condition-value-based-on-parameter.tsx';

export function CreateNotificationRuleConditionsForm() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const form = useFormContext<NotificationRuleFormData>();
  const watchRuleType = form.watch('ruleType');

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'conditions',
  });

  const addCondition = () => {
    if (fields.length >= 3) {
      return setErrorMessage('You cannot add more than 3 conditions');
    }

    return append({ fact: '', operator: 'equal', value: '' });
  };

  const removeCondition = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      setErrorMessage(undefined);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Conditions</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addCondition}
          disabled={watchRuleType === 'schedule'}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          Add Condition
        </Button>
      </div>

      {errorMessage && <StatusAlert type="error" message={errorMessage} />}

      {fields.map((field, index) => {
        const watchConditionFact = form.watch(`conditions.${index}.fact`);

        return (
          <div className="group flex flex-col" key={field.id}>
            <div
              className={cn(
                'grid grid-cols-2 flex-wrap items-start gap-2 gap-y-0',
                watchRuleType === 'schedule'
                  ? fields.length > 1
                    ? 'sm:grid-cols-[minmax(170px,_1fr)_40px]'
                    : 'sm:grid-cols-1'
                  : fields.length > 1
                    ? 'sm:grid-cols-[repeat(3,minmax(170px,_1fr))_40px]'
                    : 'sm:grid-cols-3'
              )}
            >
              <div className="flex-2">
                <ParameterComboboxSelect index={index} />
              </div>
              {watchRuleType !== 'schedule' && (
                <div className="flex-2">
                  <FormField
                    control={form.control}
                    name={`conditions.${index}.operator`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="required">Condition</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {OPERATORS.map((op) => (
                              <SelectItem key={String(op.value)} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {watchRuleType !== 'schedule' && (
                <div className="">
                  <ConditionValueBasedOnParameter
                    index={index}
                    disabled={!Number(watchConditionFact)}
                  />
                </div>
              )}

              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeCondition(index)}
                  className="mt-6 flex h-9 items-center gap-1 justify-self-start text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>

            <div className="relative h-4 group-last:hidden">
              <div className="absolute top-2 w-full border-b"></div>
              <div className="absolute -top-1.5 left-3/6 -translate-x-2/4">
                <span className="text-muted-foreground bg-white px-2 text-xs">AND</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
